import { Chip, Stack, Tooltip, Typography } from '@ds/coolshop';
import { ContentTypeGroupStatus, CtgStatus } from '@src/__generated__/types';
import React from 'react';

type ContentTypeGroupStatusesProps = {
    status: ContentTypeGroupStatus[] | undefined | null;
};

export function ContentTypeGroupStatuses(props: ContentTypeGroupStatusesProps) {
    const { status } = props;

    const [notPublished, draft, published] = React.useMemo(() => {
        if (!status) {
            return [[], [], []];
        }

        const notPublishedCtg =
            status.filter(singleStatus => singleStatus.status === CtgStatus.NotPublishable) ?? [];

        const draftCtg =
            status.filter(
                ({ status: ctgStatus }) =>
                    ctgStatus === CtgStatus.Draft || ctgStatus === CtgStatus.PublishedDraft,
            ) ?? [];

        const publishedCtg =
            status.filter(singleStatus => singleStatus.status === CtgStatus.Published) ?? [];

        return [notPublishedCtg, draftCtg, publishedCtg];
    }, [status]);

    return (
        <Stack flexDirection="row" alignItems="center" gap={0.5}>
            {notPublished.length > 0 && (
                <Tooltip
                    disableHoverListener={notPublished.length === 0}
                    title={
                        <Stack
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                        >
                            <Typography color="secondary.main" variant="subtitle2">
                                Not published
                            </Typography>
                            {notPublished.map(singleStatus => (
                                <Typography
                                    key={singleStatus.code}
                                    color="inherit"
                                    variant="subtitle2"
                                >
                                    {singleStatus.code}
                                </Typography>
                            ))}
                        </Stack>
                    }
                    placement="top"
                >
                    <Chip label={notPublished.length} size="small" color="error" />
                </Tooltip>
            )}

            {draft.length > 0 && (
                <Tooltip
                    disableHoverListener={draft.length === 0}
                    title={
                        <Stack
                            direction="column"
                            alignItems="center"
                            justifyContent="center"
                            gap={1}
                        >
                            <Typography color="secondary.main" variant="subtitle2">
                                Draft
                            </Typography>
                            {draft.map(singleStatus => (
                                <Typography
                                    key={singleStatus.code}
                                    color="inherit"
                                    variant="subtitle2"
                                >
                                    {singleStatus.code}
                                </Typography>
                            ))}
                        </Stack>
                    }
                    placement="top"
                >
                    <Chip label={draft.length} size="small" color="default" />
                </Tooltip>
            )}

            <Tooltip
                disableHoverListener={published.length === 0}
                title={
                    <Stack direction="column" alignItems="center" justifyContent="center" gap={1}>
                        <Typography color="secondary.main" variant="subtitle2">
                            Published
                        </Typography>
                        {published.map(singleStatus => (
                            <Typography key={singleStatus.code} color="inherit" variant="subtitle2">
                                {singleStatus.code}
                            </Typography>
                        ))}
                    </Stack>
                }
                placement="top"
            >
                <Chip
                    label={published.length}
                    size="small"
                    color={published.length === 0 ? 'default' : 'primary'}
                />
            </Tooltip>
        </Stack>
    );
}

export default ContentTypeGroupStatuses;
